<template>
  <Badge
    :variant="getVariant()"
    class="flex items-center gap-1"
  >
    <component
      :is="getIcon()"
      v-if="getIcon()"
      class="w-4 h-4"
    />
    <slot>
      <span class="capitalize">
        {{ props.status?.replace('_', ' ') || 'Unknown' }}
      </span>
    </slot>
  </Badge>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge'
import {
  CircleCheck,
  Loader,
  X as XIcon,
  AlertCircle,
  Archive,
  Ban,
  Clock,
  FileText
} from 'lucide-vue-next'

const props = defineProps({
  status: {type: null, required: true}
})

// Function to return variant based on status
const getVariant = () => {
  switch (props.status?.toLowerCase()) {
    // Danger/Red
    case 'overdue':
    case 'expired':
    case 'rejected':
    case 'reject':
    case 'declined':
    case 'closed':
    case 'close':
    case 'high risk':
    case 'cancelled':
    case 'returned':
    case 'flagged':
    case 'failed':
      return 'danger'
    
    // Warning/Yellow/Orange
    case 'pending':
    case 'pending_approval':
    case 'processing':
    case 'in_process':
    case 'invited':
    case 'waiting':
    case 'not_started':
    case 'in_progress':
    case 'planning':
    case 'requested':
    case 'received':
    case 'sentback':
    case 'sent_back':
    case 'awaiting_input':
    case 'in_review':
      return 'pending'
    
    // Success/Green
    case 'active':
    case 'approved':
    case 'completed':
    case 'signed':
    case 'audit_completed':
    case 'final_report_issued':
    case 'assigned':
    case 'healthy':
    case 'joined':
    case 'viewed':
    case 'acknowledged':
    case 'accepted':
    case 'ready':
      return 'success'
    
    // Neutral/Gray
    case 'under_review':
    case 'submitted':
    case 'draft_report':
      return 'warning'

    case 'inactive':
    case 'draft':
    case 'document_gathering':
    case 'monitoring_and_testing':
    case 'archived':
    case 'revoked':
    case 'not_applicable':
    case 'waived':
      return 'neutral'
      
    default:
      return 'primary'
  }
}

// Function to return icon based on status
const getIcon = () => {
  switch (props.status?.toLowerCase()) {
    // Success Icons
    case 'active':
    case 'approved':
    case 'assigned':
    case 'healthy':
    case 'completed':
    case 'signed':
    case 'audit_completed':
    case 'final_report_issued':
    case 'joined':
    case 'viewed':
    case 'acknowledged':
    case 'accepted':
    case 'ready':
      return CircleCheck
      
    // Pending/Process Icons
    case 'pending':
    case 'pending_approval':
    case 'processing':
    case 'in_process':
    case 'invited':
    case 'waiting':
    case 'not_started':
    case 'in_progress':
    case 'planning':
    case 'requested':
    case 'received':
    case 'under_review':
    case 'submitted':
    case 'sentback':
    case 'sent_back':
    case 'document_gathering':
    case 'monitoring_and_testing':
    case 'draft_report':
      return Loader
    case 'awaiting_input':
      return Clock
    case 'in_review':
      return Loader
      
    // Error/Danger Icons
    case 'overdue':
    case 'expired':
    case 'flagged':
      return AlertCircle
    case 'rejected':
    case 'reject':
    case 'declined':
    case 'closed':
    case 'close':
    case 'high risk':
    case 'cancelled':
    case 'returned':
    case 'failed':
      return XIcon
      
    // Neutral Icons
    case 'inactive':
    case 'not_applicable':
    case 'waived':
      return Ban
    case 'draft':
      return FileText
    case 'archived':
    case 'revoked':
      return Archive
      
    default:
      return CircleCheck
  }
}
</script>
